import { useState, useEffect } from 'react';
import { Users, UserPlus, UserCheck, Sparkles, ChefHat, Loader } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DiscoverFriends = () => {
  const { user } = useAuth();
  const [suggestions, setSuggestions] = useState([]);
  const [myCuisines, setMyCuisines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reason, setReason] = useState('');
  const [followedIds, setFollowedIds] = useState(new Set());

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    fetch(`${API_URL}/api/social/discover`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : {})
      .then(d => {
        setSuggestions(d.suggestions || []);
        setMyCuisines(d.my_cuisines || []);
        setReason(d.reason || '');
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user]);

  const handleFollow = async (targetId) => {
    const isFollowing = followedIds.has(targetId);
    try {
      if (isFollowing) {
        await fetch(`${API_URL}/api/social/unfollow/${targetId}`, { method: 'DELETE', credentials: 'include' });
        setFollowedIds(prev => { const s = new Set(prev); s.delete(targetId); return s; });
      } else {
        await fetch(`${API_URL}/api/social/follow/${targetId}`, { method: 'POST', credentials: 'include' });
        setFollowedIds(prev => new Set([...prev, targetId]));
      }
    } catch { /* silent */ }
  };

  if (!user) return null;

  return (
    <Card className="border border-[#FF6B35]/20 bg-gradient-to-br from-[#FFF9F0] to-white">
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 bg-gradient-to-br from-[#F4C430] to-[#FF6B35] rounded-2xl flex items-center justify-center">
            <Users className="w-4 h-4 text-white" />
          </div>
          <div>
            <h3 className="font-bold text-gray-900 text-sm">Discover Food Buddies</h3>
            <p className="text-xs text-gray-500">Based on your Foodlist cuisines</p>
          </div>
          <div className="ml-auto flex gap-1.5">
            {myCuisines.slice(0, 3).map(c => (
              <span key={c} className="text-[10px] bg-[#FFF0E5] text-[#FF6B35] px-2 py-0.5 rounded-full font-medium">{c}</span>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8 gap-2 text-gray-400">
            <Loader className="w-4 h-4 animate-spin" />
            <span className="text-sm">Finding food buddies...</span>
          </div>
        ) : reason ? (
          <div className="text-center py-6">
            <ChefHat className="w-10 h-10 text-gray-200 mx-auto mb-2" />
            <p className="text-sm text-gray-500">{reason}</p>
            <Link to="/recommendations">
              <Button size="sm" className="mt-3 bg-gradient-to-r from-[#F4C430] to-[#FF6B35] text-white">
                <Sparkles className="w-3.5 h-3.5 mr-1.5" />Explore & Save Dishes
              </Button>
            </Link>
          </div>
        ) : suggestions.length === 0 ? (
          <p className="text-center text-sm text-gray-500 py-4">No food buddies found yet — check back after more people join!</p>
        ) : (
          <div className="space-y-3">
            {suggestions.slice(0, 5).map((s) => (
              <div key={s.user_id} className="flex items-center gap-3 py-2 border-b border-gray-50 last:border-0">
                <Link to={`/profile/${s.user_id}`}>
                  <Avatar className="w-10 h-10 flex-shrink-0">
                    <AvatarImage src={s.picture} />
                    <AvatarFallback className="bg-gradient-to-br from-[#F4C430] to-[#FF6B35] text-white text-xs font-bold">
                      {s.name?.[0]?.toUpperCase() || 'F'}
                    </AvatarFallback>
                  </Avatar>
                </Link>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">{s.name}</p>
                  <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                    {s.shared_cuisines.slice(0, 2).map(c => (
                      <span key={c} className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{c}</span>
                    ))}
                    <span className="text-[10px] text-gray-400">{s.shared_count} shared</span>
                  </div>
                </div>
                <button
                  onClick={() => handleFollow(s.user_id)}
                  className={`flex-shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-2xl text-xs font-medium transition-all ${
                    followedIds.has(s.user_id)
                      ? 'bg-[#FFF5F0] text-[#FF6B35] border border-[#FF6B35]/30'
                      : 'bg-[#FF6B35] text-white hover:bg-[#E85D04]'
                  }`}
                >
                  {followedIds.has(s.user_id) ? <><UserCheck className="w-3 h-3" />Following</> : <><UserPlus className="w-3 h-3" />Follow</>}
                </button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DiscoverFriends;
